/**
 * LivePage — multi-camera live monitoring.
 * Default: 4 streams. Options: 2, 4, 6, 8, All.
 * Supervisor sees only operators under them.
 * Admin sees all, can filter by location.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, ButtonGroup, Chip, CircularProgress, FormControl,
  Grid, InputLabel, MenuItem, Paper, Select, Stack, Tooltip, Typography,
} from '@mui/material';
import VideocamOffIcon   from '@mui/icons-material/VideocamOff';
import RefreshIcon       from '@mui/icons-material/Refresh';
import FullscreenIcon    from '@mui/icons-material/Fullscreen';
import StreamViewer      from '../components/StreamViewer';
import { getDevices, getLocations, isAdmin, isSupervisor } from '../api/client';
import useDetectionSettings, { enabled } from '../hooks/useDetectionSettings';

const GRID_OPTIONS = [2, 4, 6, 8, 'All'];

const HEIGHTS = { 2: 440, 4: 300, 6: 240, 8: 200, All: 180 };

function StreamPanel({ device, height, detectionSettings }) {
  if (!device) return (
    <Box sx={{ height, bgcolor: '#111', borderRadius: 1, border: '1px dashed rgba(255,255,255,0.12)',
               display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <VideocamOffIcon sx={{ color: 'rgba(255,255,255,0.15)', fontSize: 36 }} />
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', mt: 0.5 }}>No device</Typography>
    </Box>
  );

  const status = device.current_operator
    ? (device.current_operator.status === 'break' ? 'On Break' : 'Active')
    : 'No Operator';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, px: 0.5 }}>
        <Typography variant="caption" fontWeight={700} noWrap sx={{ maxWidth: 120 }}>
          {device.hostname || device.id.slice(0, 8)}
        </Typography>
        <Chip size="small" label={device.is_online ? 'Online' : 'Offline'}
          color={device.is_online ? 'success' : 'default'} sx={{ height: 16, fontSize: '0.6rem' }} />
        {device.current_operator && (
          <Chip size="small" label={device.current_operator.name}
            sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#1565c0', color: 'white' }} />
        )}
        {enabled(detectionSettings, 'presence') && device.presence && (
          <Chip size="small"
            label={`📍 ${device.presence.current_status?.replace('_', ' ') || 'inactive'} · ${device.presence.presence_percent}%`}
            sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#00695c', color: 'white' }} />
        )}
        {enabled(detectionSettings, 'drowsiness') && (device.drowsiness_count > 0) && (
          <Chip size="small" label={`😴${device.drowsiness_count}`}
            sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#c62828', color: 'white' }} />
        )}
        {enabled(detectionSettings, 'yawning') && (device.yawn_count > 0) && (
          <Chip size="small" label={`🥱${device.yawn_count}`}
            sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#ef6c00', color: 'white' }} />
        )}
        {enabled(detectionSettings, 'distracted') && (device.distracted_count > 0) && (
          <Chip size="small" label={`👀${device.distracted_count}`}
            sx={{ height: 16, fontSize: '0.6rem', bgcolor: '#9c27b0', color: 'white' }} />
        )}
      </Box>
      <StreamViewer deviceId={device.id} deviceName={device.hostname} height={height} showOverlay />
    </Box>
  );
}

export default function LivePage() {
  const detectionSettings = useDetectionSettings();
  const hasAlert = (device) =>
    (enabled(detectionSettings, 'drowsiness') && device.drowsiness_count > 0)
    || (enabled(detectionSettings, 'yawning') && device.yawn_count > 0)
    || (enabled(detectionSettings, 'distracted') && device.distracted_count > 0);
  const [devices,   setDevices]   = useState([]);
  const [locations, setLocations] = useState([]);
  const [locFilter, setLocFilter] = useState('');
  const [gridCount, setGridCount] = useState(4);
  const [loading,   setLoading]   = useState(false);
  const [page,      setPage]      = useState(0); // pagination for large grids

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [devs, locs] = await Promise.all([
        getDevices(),
        isAdmin() ? getLocations() : Promise.resolve([]),
      ]);
      setDevices(devs);
      setLocations(locs);
      setPage(0);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter by location if admin
  const filtered = locFilter
    ? devices.filter(d => d.location_id === locFilter)
    : devices;

  // Which devices to show on current page
  const perPage = gridCount === 'All' ? filtered.length : gridCount;
  const totalPages = Math.ceil(filtered.length / perPage);
  const visible = filtered.slice(page * perPage, page * perPage + perPage);
  // Pad to fill grid
  const slots = gridCount === 'All'
    ? visible
    : [...visible, ...Array(Math.max(0, perPage - visible.length)).fill(null)];

  const cols = gridCount === 2 ? 6 : gridCount <= 4 ? 6 : 4; // MUI xs cols per item

  return (
    <Box sx={{ p: 2, bgcolor: '#0a0a0a', minHeight: '100%' }}>
      {/* Controls */}
      <Stack direction="row" spacing={2} alignItems="center" mb={2} flexWrap="wrap">
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
          Live Monitoring
        </Typography>

        {isAdmin() && locations.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ color: '#aaa' }}>Location</InputLabel>
            <Select value={locFilter} label="Location"
              onChange={e => { setLocFilter(e.target.value); setPage(0); }}
              sx={{ color: 'white', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}>
              <MenuItem value="">All Locations</MenuItem>
              {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
            </Select>
          </FormControl>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Grid size selector */}
        <ButtonGroup size="small" variant="outlined"
          sx={{ '& .MuiButton-root': { color: '#ccc', borderColor: '#444' } }}>
          {GRID_OPTIONS.map(n => (
            <Button key={n}
              variant={gridCount === n ? 'contained' : 'outlined'}
              onClick={() => { setGridCount(n); setPage(0); }}
              sx={gridCount === n ? { bgcolor: '#1565c0' } : {}}>
              {n === 'All' ? 'All' : `${n}`}
            </Button>
          ))}
        </ButtonGroup>

        <Tooltip title="Refresh device list">
          <Button size="small" variant="outlined"
            sx={{ color: '#ccc', borderColor: '#444' }}
            onClick={load} startIcon={<RefreshIcon />}>
            {loading ? <CircularProgress size={14} sx={{ color: 'white' }} /> : 'Refresh'}
          </Button>
        </Tooltip>
      </Stack>

      {/* Stats bar */}
      <Stack direction="row" spacing={2} mb={2}>
        <Chip label={`${filtered.length} devices`}
          sx={{ bgcolor: '#1a1a1a', color: '#ccc', fontSize: '0.75rem' }} />
        <Chip label={`${filtered.filter(d => d.is_online).length} online`}
          sx={{ bgcolor: '#1b5e20', color: 'white', fontSize: '0.75rem' }} />
        <Chip label={`${filtered.filter(d => d.current_operator).length} active operators`}
          sx={{ bgcolor: '#0d47a1', color: 'white', fontSize: '0.75rem' }} />
        {filtered.filter(hasAlert).length > 0 && (
          <Chip label={`${filtered.filter(hasAlert).length} with alerts`}
            sx={{ bgcolor: '#b71c1c', color: 'white', fontSize: '0.75rem' }} />
        )}
      </Stack>

      {/* Grid */}
      <Grid container spacing={1.5}>
        {slots.map((device, i) => (
          <Grid item xs={12} sm={cols} key={device?.id || `empty-${i}`}>
            <StreamPanel device={device} height={HEIGHTS[gridCount] || 200}
              detectionSettings={detectionSettings} />
          </Grid>
        ))}
      </Grid>

      {/* Pagination (when not showing All) */}
      {gridCount !== 'All' && totalPages > 1 && (
        <Stack direction="row" justifyContent="center" spacing={1} mt={2}>
          <Button size="small" disabled={page === 0} onClick={() => setPage(p => p - 1)}
            sx={{ color: '#ccc', borderColor: '#444' }} variant="outlined">
            Prev
          </Button>
          {[...Array(totalPages)].map((_, i) => (
            <Button key={i} size="small"
              variant={page === i ? 'contained' : 'outlined'}
              onClick={() => setPage(i)}
              sx={page === i ? { bgcolor: '#1565c0' } : { color: '#ccc', borderColor: '#444' }}>
              {i + 1}
            </Button>
          ))}
          <Button size="small" disabled={page === totalPages - 1} onClick={() => setPage(p => p + 1)}
            sx={{ color: '#ccc', borderColor: '#444' }} variant="outlined">
            Next
          </Button>
        </Stack>
      )}
    </Box>
  );
}
